import { collection, setDoc, updateDoc, deleteDoc, doc, getDocs, query, where, orderBy } from "firebase/firestore";
import { db } from "./firebase";
import { accountingRecordConverter, accountingRecordSchema } from "../models/accountingRecord";
import AuditService, { AUDIT_ACTIONS, AUDIT_MODULES } from "./audit_service";
import { COLLECTIONS } from "./collections";
import { formatCurrency } from "../utils/format";


/**
 * AccountingRecordService
 * O coração do sistema Gerencial DRE (Livro Contábil / Regime de Competência).
 * 
 * Coleção: tenants/{idTenant}/accountingRecords
 */
const AccountingRecordService = {

    // =====================================================================
    // LEITURA (DRE)
    // =====================================================================

    /**
     * Busca os registros contábeis por DATA DE COMPETÊNCIA.
     * Consulta principal do novo Dashboard e telas do DRE.
     * 
     * @param {string} idTenant 
     * @param {Date|string} startDate 
     * @param {Date|string} endDate 
     * @param {string} [type] - INCOME ou EXPENSE
     */
    getByCompetenceDate: async (idTenant, startDate, endDate, type = null) => {
        if (!idTenant) throw new Error("ID do Tenant é obrigatório.");
        try {
            const startStr = typeof startDate === 'string' ? startDate : startDate.toISOString().split('T')[0];
            const endStr = typeof endDate === 'string' ? endDate : endDate.toISOString().split('T')[0];

            const constraints = [
                where("competenceDate", ">=", startStr),
                where("competenceDate", "<=", endStr),
                orderBy("competenceDate", "asc")
            ];

            if (type) {
                constraints.unshift(where("type", "==", type));
            }

            const q = query(
                collection(db, "tenants", idTenant, COLLECTIONS.ACCOUNTING_RECORDS),
                ...constraints
            );

            const snapshot = await getDocs(q);
            // Apply converter manually to match architecture from models
            return snapshot.docs.map(docSnapshot => accountingRecordConverter.fromFirestore(docSnapshot));
        } catch (error) {
            console.error("Erro ao buscar registros contábeis por competência:", error);
            throw error;
        }
    },

    /**
     * Busca o faturamento Global associado a uma Categoria Específica na Competência.
     */
    getByCategoryAndCompetenceDate: async (idTenant, categoryId, startDate, endDate) => {
        if (!idTenant || !categoryId) throw new Error("ID do Tenant e CategoryId são obrigatórios.");
        try {
            const startStr = typeof startDate === 'string' ? startDate : startDate.toISOString().split('T')[0];
            const endStr = typeof endDate === 'string' ? endDate : endDate.toISOString().split('T')[0];

            const q = query(
                collection(db, "tenants", idTenant, COLLECTIONS.ACCOUNTING_RECORDS),
                where("idCategory", "==", categoryId),
                where("competenceDate", ">=", startStr),
                where("competenceDate", "<=", endStr),
                orderBy("competenceDate", "asc")
            );

            const snapshot = await getDocs(q);
            return snapshot.docs.map(docSnapshot => accountingRecordConverter.fromFirestore(docSnapshot));
        } catch (error) {
            console.error("Erro ao buscar DRE por categoria:", error);
            throw error;
        }
    },

    // =====================================================================
    // ESCRITA (Manuais ou Correções Gerenciais de DRE)
    // =====================================================================

    /**
     * Salva manual (Cria ou Edita Custo/Receita Fantasma de DRE)
     */
    save: async (formData, idTenant, authUser, batch = null) => {
        if (!idTenant) throw new Error("ID do Tenant é obrigatório.");

        // Validação rígida de Schema (Blindagem contra dados corrompidos)
        try {
            accountingRecordSchema.validateSync(formData, { abortEarly: false, stripUnknown: true });
        } catch (validationError) {
            console.error("Erro de Validação no AccountingRecord:", validationError.errors);
            throw new Error(`Dados inválidos para DRE: ${validationError.errors.join(", ")}`);
        }

        // Usa o toFirestore do conversor (mantém padrão puro sem side effects no Model)
        const recordData = accountingRecordConverter.toFirestore(formData);

        // Tags de autoria manuais
        recordData.updatedBy = {
            uid: authUser?.uid || "system",
            name: authUser?.displayName || authUser?.fullName || authUser?.email || "Sistema"
        };

        try {
            if (formData.id) {
                // UPDATE
                const recordRef = doc(db, "tenants", idTenant, COLLECTIONS.ACCOUNTING_RECORDS, formData.id);
                if (batch) {
                    batch.update(recordRef, recordData);
                } else {
                    await updateDoc(recordRef, recordData);
                }

                AuditService.log({
                    idTenant,
                    user: authUser,
                    module: AUDIT_MODULES.FINANCIAL_RECORDS,
                    action: AUDIT_ACTIONS.UPDATE,
                    entityId: formData.id,
                    entityName: `${recordData.type} (DRE)`,
                    description: `Atualizou Livro Contábil de valor ${formatCurrency(recordData.amount)}`,
                });

                return { id: formData.id, ...recordData };
            } else {
                // CREATE
                recordData.createdBy = recordData.updatedBy;
                const newDocRef = doc(collection(db, "tenants", idTenant, COLLECTIONS.ACCOUNTING_RECORDS));

                if (batch) {
                    batch.set(newDocRef, recordData);
                } else {
                    await setDoc(newDocRef, recordData);
                }

                AuditService.log({
                    idTenant,
                    user: authUser,
                    module: AUDIT_MODULES.FINANCIAL_RECORDS,
                    action: AUDIT_ACTIONS.CREATE,
                    entityId: newDocRef.id,
                    entityName: `${recordData.type} (DRE)`,
                    description: `Lançou Registro Contábil DRE manualmente`
                });

                return { id: newDocRef.id, ...recordData };
            }
        } catch (error) {
            console.error("Erro no AccountingRecordService.save:", error);
            throw error;
        }
    },

    /**
     * Deletar Fato Gerador/Erro contábil
     */
    delete: async (recordId, idTenant, authUser) => {
        if (!idTenant) throw new Error("ID do Tenant é obrigatório.");
        try {
            await deleteDoc(doc(db, "tenants", idTenant, COLLECTIONS.ACCOUNTING_RECORDS, recordId));

            AuditService.log({
                idTenant,
                user: authUser,
                module: AUDIT_MODULES.FINANCIAL_RECORDS,
                action: AUDIT_ACTIONS.DELETE,
                entityId: recordId,
                entityName: "Remoção DRE",
                description: `Excluiu lançamento contábil raiz.`
            });

            return true;
        } catch (error) {
            console.error("Erro no AccountingRecordService.delete:", error);
            throw error;
        }
    }
};

export default AccountingRecordService;
